import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';

export type UploadFileStatus =
    | 'pending'
    | 'uploading'
    | 'success'
    | 'error'
    | 'skipped'
    | 'cancelled';

export interface UploadFileItem {
    id: string;
    file: File;
    status: UploadFileStatus;
    progress: number;
    bytesUploaded: number;
    error?: string;
    attempts: number;
    /** Whether this file is a previewable image. The actual preview object URL
     * is created lazily on demand via `requestPreview(id)` so we don't allocate
     * thousands of <img>-backed object URLs up front. */
    isPreviewable: boolean;
    shareIdentifier: string;
}

export interface UploadSummary {
    total: number;
    pending: number;
    uploading: number;
    success: number;
    failed: number;
    skipped: number;
    bytesTotal: number;
    bytesUploaded: number;
    overallProgress: number;
}

interface UseBulkUploaderOptions {
    activationId: number;
    photoSessionId?: string;
    concurrency?: number;
    maxFileSizeBytes?: number;
    runProcessingFlow?: boolean;
}

interface UseBulkUploaderResult {
    files: UploadFileItem[];
    summary: UploadSummary;
    isUploading: boolean;
    isPaused: boolean;
    hasFinished: boolean;
    addFiles: (files: File[]) => { added: number; rejected: { file: File; reason: string }[] };
    removeFile: (id: string) => void;
    skipFile: (id: string) => void;
    retryFile: (id: string) => void;
    retryAllFailed: () => void;
    startUpload: () => void;
    pause: () => void;
    resume: () => void;
    cancelAll: () => void;
    clearAll: () => void;
    /** Lazily allocates (and caches) an object URL for the given file id so
     * previews are only materialized when actually rendered. Returns undefined
     * for non-previewable files or unknown ids. Cached URLs are revoked when
     * the file is removed/cleared or the hook unmounts. */
    requestPreview: (id: string) => string | undefined;
}

const DEFAULT_CONCURRENCY = 4;

const isPreviewableImage = (file: File) =>
    file.type.startsWith('image/') && !file.type.includes('heic') && !file.type.includes('heif');

export default function useBulkUploader({
    activationId,
    photoSessionId,
    concurrency = DEFAULT_CONCURRENCY,
    maxFileSizeBytes,
    runProcessingFlow = false,
}: UseBulkUploaderOptions): UseBulkUploaderResult {
    const api = useAuthenticatedApi();

    const [files, setFiles] = useState<UploadFileItem[]>([]);
    const [isPaused, setIsPaused] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const filesRef = useRef<UploadFileItem[]>([]);
    const queueRef = useRef<string[]>([]);
    const runningRef = useRef(0);
    const pausedRef = useRef(false);
    const cancellersRef = useRef<Map<string, CancelTokenSource>>(new Map());
    const previewUrlsRef = useRef<Map<string, string>>(new Map());
    const runProcessingFlowRef = useRef(runProcessingFlow);

    filesRef.current = files;
    runProcessingFlowRef.current = runProcessingFlow;

    useEffect(() => {
        // Capture the ref maps at effect-setup time. The lint rule warns
        // because by the cleanup time the `.current` could be reassigned, but
        // these are stable Map instances we mutate in place, so capture once.
        const previewUrls = previewUrlsRef.current;
        const cancellers = cancellersRef.current;
        return () => {
            // Revoke any preview URLs we created to prevent leaks.
            previewUrls.forEach((url) => URL.revokeObjectURL(url));
            previewUrls.clear();
            // Abort any in-flight requests.
            cancellers.forEach((source) =>
                source.cancel('Component unmounted'),
            );
            cancellers.clear();
        };
    }, []);

    const updateFile = useCallback(
        (id: string, patch: Partial<UploadFileItem>) => {
            setFiles((prev) =>
                prev.map((f) => (f.id === id ? { ...f, ...patch } : f)),
            );
        },
        [],
    );

    const uploadOne = useCallback(
        async (item: UploadFileItem) => {
            const cancelSource = axios.CancelToken.source();
            cancellersRef.current.set(item.id, cancelSource);

            const useProcessingEndpoint = runProcessingFlowRef.current;

            const formData = new FormData();
            formData.append('photo', item.file);

            let url: string;
            if (useProcessingEndpoint) {
                // Single-file endpoint runs cloud processing flow (env=2) when
                // present and `alreadyProcessed` is false.
                url = `/media/upload/${activationId}`;
                formData.append('shareIdentifier', item.shareIdentifier);
                formData.append('alreadyProcessed', 'false');
                if (photoSessionId) {
                    formData.append('photoSessionId', photoSessionId);
                }
            } else {
                url = `/media/upload/${activationId}/bulk${
                    photoSessionId ? `/${photoSessionId}` : ''
                }`;
            }

            try {
                const response = await api.post(url, formData, {
                    cancelToken: cancelSource.token,
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (e: {
                        loaded: number;
                        total?: number;
                    }) => {
                        if (!e.total) return;
                        const progress = Math.min(
                            100,
                            Math.round((e.loaded / e.total) * 100),
                        );
                        updateFile(item.id, {
                            progress,
                            bytesUploaded: e.loaded,
                        });
                    },
                });

                if (response.status >= 200 && response.status < 300) {
                    updateFile(item.id, {
                        status: 'success',
                        progress: 100,
                        bytesUploaded: item.file.size,
                        error: undefined,
                    });
                } else {
                    throw new Error(
                        `Upload failed with status ${response.status}`,
                    );
                }
            } catch (e: any) {
                if (axios.isCancel(e)) {
                    updateFile(item.id, {
                        status: 'cancelled',
                        progress: 0,
                        bytesUploaded: 0,
                    });
                } else {
                    const message =
                        e?.response?.data?.message ||
                        e?.response?.statusText ||
                        e?.message ||
                        'Upload failed';
                    updateFile(item.id, {
                        status: 'error',
                        error: message,
                    });
                }
            } finally {
                cancellersRef.current.delete(item.id);
                runningRef.current = Math.max(0, runningRef.current - 1);
                processQueueRef.current();
            }
        },
        [activationId, photoSessionId, api, updateFile],
    );

    const processQueueRef = useRef<() => void>(() => {});

    const processQueue = useCallback(() => {
        if (pausedRef.current) {
            return;
        }

        while (
            runningRef.current < concurrency &&
            queueRef.current.length > 0
        ) {
            const nextId = queueRef.current.shift();
            if (!nextId) break;

            const file = filesRef.current.find((f) => f.id === nextId);
            // Skip if the file was removed/skipped/already succeeded between enqueue and dequeue.
            if (!file || file.status !== 'pending') {
                continue;
            }

            runningRef.current += 1;
            updateFile(nextId, {
                status: 'uploading',
                progress: 0,
                bytesUploaded: 0,
                error: undefined,
                attempts: file.attempts + 1,
            });
            // Fire and forget; uploadOne handles its own state.
            uploadOne({ ...file, status: 'uploading', attempts: file.attempts + 1 });
        }

        // If nothing is running and queue is empty, mark uploading complete.
        if (runningRef.current === 0 && queueRef.current.length === 0) {
            setIsUploading(false);
        }
    }, [concurrency, updateFile, uploadOne]);

    processQueueRef.current = processQueue;

    const addFiles = useCallback<UseBulkUploaderResult['addFiles']>(
        (incoming) => {
            const rejected: { file: File; reason: string }[] = [];
            const accepted: UploadFileItem[] = [];

            for (const f of incoming) {
                if (maxFileSizeBytes && f.size > maxFileSizeBytes) {
                    rejected.push({ file: f, reason: 'File exceeds size limit' });
                    continue;
                }

                const id = uuidv4();

                accepted.push({
                    id,
                    file: f,
                    status: 'pending',
                    progress: 0,
                    bytesUploaded: 0,
                    attempts: 0,
                    isPreviewable: isPreviewableImage(f),
                    // Stable shareIdentifier per file; reused across retries so
                    // the single-file upload endpoint is idempotent.
                    shareIdentifier: `${activationId}_${id}`,
                });
            }

            if (accepted.length === 0) {
                return { added: 0, rejected };
            }

            setFiles((prev) => [...prev, ...accepted]);

            // If an upload session is already in progress, queue these too.
            // (When paused, processQueue is a no-op until resume runs it.)
            if (isUploading) {
                queueRef.current.push(...accepted.map((f) => f.id));
                if (!pausedRef.current) {
                    // Defer to next tick so state has updated before processing.
                    setTimeout(() => processQueueRef.current(), 0);
                }
            }

            return { added: accepted.length, rejected };
        },
        [maxFileSizeBytes, isUploading, activationId],
    );

    const requestPreview = useCallback<UseBulkUploaderResult['requestPreview']>(
        (id) => {
            const existing = previewUrlsRef.current.get(id);
            if (existing) return existing;

            const item = filesRef.current.find((f) => f.id === id);
            if (!item || !item.isPreviewable) return undefined;

            try {
                const url = URL.createObjectURL(item.file);
                previewUrlsRef.current.set(id, url);
                return url;
            } catch {
                return undefined;
            }
        },
        [],
    );

    const releasePreview = useCallback((id: string) => {
        const url = previewUrlsRef.current.get(id);
        if (url) {
            URL.revokeObjectURL(url);
            previewUrlsRef.current.delete(id);
        }
    }, []);

    const removeFile = useCallback<UseBulkUploaderResult['removeFile']>(
        (id) => {
            const cancelSource = cancellersRef.current.get(id);
            if (cancelSource) {
                cancelSource.cancel('Removed by user');
            }
            queueRef.current = queueRef.current.filter((qid) => qid !== id);
            releasePreview(id);
            setFiles((prev) => prev.filter((f) => f.id !== id));
        },
        [releasePreview],
    );

    const skipFile = useCallback<UseBulkUploaderResult['skipFile']>(
        (id) => {
            const cancelSource = cancellersRef.current.get(id);
            if (cancelSource) {
                cancelSource.cancel('Skipped by user');
            }
            queueRef.current = queueRef.current.filter((qid) => qid !== id);
            updateFile(id, { status: 'skipped', error: undefined });
        },
        [updateFile],
    );

    const retryFile = useCallback<UseBulkUploaderResult['retryFile']>(
        (id) => {
            const file = filesRef.current.find((f) => f.id === id);
            if (!file) return;
            if (file.status === 'uploading') return;

            updateFile(id, {
                status: 'pending',
                progress: 0,
                bytesUploaded: 0,
                error: undefined,
            });

            queueRef.current.push(id);
            setIsUploading(true);
            setTimeout(() => processQueueRef.current(), 0);
        },
        [updateFile],
    );

    const retryAllFailed = useCallback(() => {
        const failedIds = filesRef.current
            .filter((f) => f.status === 'error' || f.status === 'cancelled')
            .map((f) => f.id);

        if (failedIds.length === 0) return;

        setFiles((prev) =>
            prev.map((f) =>
                failedIds.includes(f.id)
                    ? {
                          ...f,
                          status: 'pending',
                          progress: 0,
                          bytesUploaded: 0,
                          error: undefined,
                      }
                    : f,
            ),
        );

        queueRef.current.push(...failedIds);
        setIsUploading(true);
        setTimeout(() => processQueueRef.current(), 0);
    }, []);

    const startUpload = useCallback(() => {
        const pendingIds = filesRef.current
            .filter((f) => f.status === 'pending')
            .map((f) => f.id);

        if (pendingIds.length === 0) return;

        // Avoid double-enqueueing the same id if user clicks start twice.
        const queueSet = new Set(queueRef.current);
        for (const id of pendingIds) {
            if (!queueSet.has(id)) {
                queueRef.current.push(id);
            }
        }

        pausedRef.current = false;
        setIsPaused(false);
        setIsUploading(true);
        processQueueRef.current();
    }, []);

    const pause = useCallback(() => {
        pausedRef.current = true;
        setIsPaused(true);
    }, []);

    const resume = useCallback(() => {
        pausedRef.current = false;
        setIsPaused(false);
        processQueueRef.current();
    }, []);

    const cancelAll = useCallback(() => {
        queueRef.current = [];
        cancellersRef.current.forEach((source) => source.cancel('Cancelled by user'));
        cancellersRef.current.clear();
        runningRef.current = 0;
        pausedRef.current = false;
        setIsPaused(false);
        setIsUploading(false);
        setFiles((prev) =>
            prev.map((f) =>
                f.status === 'uploading' || f.status === 'pending'
                    ? { ...f, status: 'cancelled', progress: 0, bytesUploaded: 0 }
                    : f,
            ),
        );
    }, []);

    const clearAll = useCallback(() => {
        // Cancel anything in-flight, drop the queue, release previews.
        cancellersRef.current.forEach((source) => source.cancel('Cleared'));
        cancellersRef.current.clear();
        queueRef.current = [];
        runningRef.current = 0;
        previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        previewUrlsRef.current.clear();
        pausedRef.current = false;
        setIsPaused(false);
        setIsUploading(false);
        setFiles([]);
    }, []);

    const summary = useMemo<UploadSummary>(() => {
        let pending = 0;
        let uploading = 0;
        let success = 0;
        let failed = 0;
        let skipped = 0;
        let bytesTotal = 0;
        let bytesUploaded = 0;

        for (const f of files) {
            // Skipped files do not count toward total bytes.
            if (f.status !== 'skipped') {
                bytesTotal += f.file.size;
                bytesUploaded += f.bytesUploaded;
            }
            switch (f.status) {
                case 'pending':
                    pending += 1;
                    break;
                case 'uploading':
                    uploading += 1;
                    break;
                case 'success':
                    success += 1;
                    break;
                case 'error':
                case 'cancelled':
                    failed += 1;
                    break;
                case 'skipped':
                    skipped += 1;
                    break;
            }
        }

        const total = files.length;
        const overallProgress =
            bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0;

        return {
            total,
            pending,
            uploading,
            success,
            failed,
            skipped,
            bytesTotal,
            bytesUploaded,
            overallProgress,
        };
    }, [files]);

    const hasFinished =
        files.length > 0 &&
        !isUploading &&
        summary.pending === 0 &&
        summary.uploading === 0 &&
        (summary.success > 0 || summary.failed > 0 || summary.skipped > 0);

    return {
        files,
        summary,
        isUploading,
        isPaused,
        hasFinished,
        addFiles,
        removeFile,
        skipFile,
        retryFile,
        retryAllFailed,
        startUpload,
        pause,
        resume,
        cancelAll,
        clearAll,
        requestPreview,
    };
}
